import React, { useState, useEffect, useCallback } from 'react';
import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	Legend,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	ResponsiveContainer,
	LineChart,
	Line,
	LabelList,
} from 'recharts';
import moment from 'moment';
import { request } from '../../../services/utilities';
import TableLoading from '../..//../components/TableLoading';
import { analyticsAPI } from '../../../services/constants';
import AnalysisBystaff from '../../../components/Modals/frontDesk/staffAnalyticsmodal';
import { TimeRangeDropdownAlt3 as TimeRangeDropdown } from '../../../components/TimeRangeDropdownAlt3';

// const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ff6e54'];

const PharmacyStaffAnalytics = ({ selectedStaff }) => {
	const [timeRanges, setTimeRanges] = useState({
		topRequests: 'past30Days',
		pharmacyTrends: 'past30Days',
		drugWaste: 'past30Days',
		paymentTypes: 'past30Days',
		substitutions: 'past30Days',
		cancelledDrugs: 'past30Days',
		requestMetrics: 'past30Days',
	});

	const [loadingStates, setLoadingStates] = useState({
		topRequests: false,
		paymentTypes: false,
		pharmacyTrends: false,
		drugWaste: false,
		substitutions: false,
		cancelledDrugs: false,
		metrics: false,
	});

	const [topRequests, setTopRequests] = useState([]);
	const [paymentTypes, setPaymentTypes] = useState([]);
	const [pharmacyTrends, setPharmacyTrends] = useState([]);
	const [drugWaste, setDrugWaste] = useState([]);
	const [substitutions, setSubstitutions] = useState([]);
	const [cancelledDrugs, setCancelledDrugs] = useState([]);
	const [requestMetrics, setRequestMetrics] = useState({
		totalRequests: '0',
		notReceived: null,
		received: null,
		notSubstituted: null,
		substituted: null,
		dateScheduled: null,
		dateNotScheduled: null,
		notCancelled: null,
		cancelled: null,
		notApproved: null,
		approved: null,
		notFilled: null,
		filled: null,
		deleted: null,
		cancelledOnly: null,
		totalCancelled: 0,
		deletedCount: 0,
		cancelledOnlyCount: 0,
	});

	const mapTimeRangeToApiFilter = range => {
		switch (range) {
			case 'today':
				return 'today';
			case 'pastWeek':
				return 'past-week';
			case 'past30Days':
				return 'past-month';
			case 'lastMonth':
				return 'last-month';
			case 'pastQuarter':
				return 'past-quarter';
			case 'pastSixMonths':
				return 'past-six-months';
			case 'pastYear':
				return 'past-year';
			case 'lastYear':
				return 'last-year';
			case 'pastThreeYears':
				return 'past-three-years';
			case 'allTime':
				return 'all-time';
			default:
				return 'past-month';
		}
	};

	const fetchTopMeds = async range => {
		setLoadingStates(prev => ({ ...prev, topRequests: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilter(range);
			const res = await request(`analytics/pharmacy-top-ten?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name: x?.name,
				requests: parseInt(x?.count, 10),
			}));
			setTopRequests(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch top medications:', error);
			setTopRequests([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, topRequests: false }));
		}
	};

	const fetchDrugWaste = async range => {
		setLoadingStates(prev => ({ ...prev, drugWaste: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilter(range);
			const res = await request(`analytics/pharmacy-old?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiResponse = res?.query?.map(x => ({
				name: x?.drug?.name,
				requests: x?.quantity,
			}));
			setDrugWaste(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch drug waste:', error);
			setDrugWaste([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, drugWaste: false }));
		}
	};

	const fetchPaymentType = async range => {
		setLoadingStates(prev => ({ ...prev, paymentTypes: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilter(range);
			const res = await request(`analytics/pharmacy-payment-type?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name: x?.name,
				value: parseInt(x?.count, 10),
			}));
			setPaymentTypes(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch payment types:', error);
			setPaymentTypes([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, paymentTypes: false }));
		}
	};

	const fetchCancelledDrugs = async range => {
		setLoadingStates(prev => ({ ...prev, cancelledDrugs: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilter(range);
			const res = await request(`analytics/cancelled-drug-trend?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name:
					x?.date ||
					(dateFilter == 'past-three-years' && x?.month && moment(x?.month).format('MMMM, YY')) ||
					(x?.month && moment(x?.month).format('MMMM')) ||
					x?.day ||
					x?.week ||
					x?.year ||
					x?.hour,
				value: parseInt(x?.count, 10),
			}));
			setCancelledDrugs(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch cancelled drugs:', error);
			setCancelledDrugs([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, cancelledDrugs: false }));
		}
	};

	const fetchPharmacyTrends = async range => {
		setLoadingStates(prev => ({ ...prev, pharmacyTrends: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilter(range);
			const res = await request(`analytics/pharmacy-trends-type?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name:
					x?.date ||
					(dateFilter == 'past-three-years' && x?.month && moment(x?.month).format('MMMM, YY')) ||
					(x?.month && moment(x?.month).format('MMMM')) ||
					x?.day ||
					x?.week ||
					x?.year ||
					x?.hour,
				value: parseInt(x?.count, 10),
			}));
			setPharmacyTrends(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch pharmacy trends:', error);
			setPharmacyTrends([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, pharmacyTrends: false }));
		}
	};

	const fetchSubstitutions = async range => {
		setLoadingStates(prev => ({ ...prev, substitutions: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilter(range);
			const res = await request(`analytics/sub-trends?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name:
					x?.date ||
					(dateFilter == 'past-three-years' && x?.month && moment(x?.month).format('MMMM, YY')) ||
					(x?.month && moment(x?.month).format('MMMM')) ||
					x?.day ||
					x?.week ||
					x?.year ||
					x?.hour,
				value: parseInt(x?.count, 10),
			}));
			setSubstitutions(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch substitutions:', error);
			setSubstitutions([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, substitutions: false }));
		}
	};

	const fetchMetrics = async range => {
		setLoadingStates(prev => ({ ...prev, metrics: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilter(range);
			const url = `${analyticsAPI}/patient-request-metrics?dateFilter=${dateFilter}&requestType=drugs&staffId=${selectedStaff?.id}`;

			// actually fetch
			const res = await request(url, 'GET', true);

			setRequestMetrics(res?.result || {});
		} catch (error) {
			console.error('Failed to fetch metrics:', error);
			setRequestMetrics({});
		} finally {
			setLoadingStates(prev => ({ ...prev, metrics: false }));
		}
	};

	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));
		switch (chartId) {
			case 'topRequests':
				fetchTopMeds(range);
				break;
			case 'pharmacyTrends':
				fetchPharmacyTrends(range);
				break;
			case 'drugWaste':
				fetchDrugWaste(range);
				break;
			case 'paymentTypes':
				fetchPaymentType(range);
				break;
			case 'substitutions':
				fetchSubstitutions(range);
				break;
			case 'cancelledDrugs':
				fetchCancelledDrugs(range);
				break;
			case 'requestMetrics':
				fetchMetrics(range);
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		fetchTopMeds(timeRanges.topRequests);
		fetchPharmacyTrends(timeRanges.pharmacyTrends);
		fetchDrugWaste(timeRanges.drugWaste);
		fetchPaymentType(timeRanges.paymentTypes);
		fetchSubstitutions(timeRanges.substitutions);
		fetchCancelledDrugs(timeRanges.cancelledDrugs);
		fetchMetrics(timeRanges.requestMetrics);
	}, []);

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	const pharmacyMetricsConfig = [
		{ key: 'totalRequests', label: 'Total Requests' },
		{ key: 'approved', label: 'Approved' },
		{ key: 'notApproved', label: 'Not Approved' },
		{ key: 'cancelled', label: 'Cancelled' },
		{ key: 'cancelledOnlyCount', label: 'Cancelled Only' },
		{ key: 'filled', label: 'Filled' },
		{ key: 'notFilled', label: 'Not Filled' },
		{ key: 'substituted', label: 'Substituted' },
		{ key: 'notSubstituted', label: 'Not Substituted' },
		{ key: 'received', label: 'Received' },
		{ key: 'notReceived', label: 'Not Received' },
		{ key: 'deleted', label: 'Deleted' },
	];

	const MetricCard = ({ loading, value, label, total, color = 'primary' }) => {
		const percentage = value != null && total > 0 ? ((value / total) * 100).toFixed(1) : 0;
		return (
			<div className="col-sm-3">
				<a className="element-box el-tablo" href="#">
					<div className="label">{label}</div>
					<div className={`value`}>{loading ? <TableLoading /> : value != null ? Number(value)?.toLocaleString() : '0'}</div>
					<div className={`trending ${percentage > 0 ? 'trending-up-basic' : 'trending-down-basic'}`}>
						<span>{percentage > 0 ? percentage : '0'}%</span>
						{percentage > 0 ? <i className="os-icon os-icon-arrow-up2"></i> : <i className="os-icon os-icon-arrow-down"></i>}
					</div>
				</a>
			</div>
		);
	};

	const totalValue = Number(requestMetrics?.totalRequests) || 0;

	return (
		<div className="content-i">
			<div className="content-box">
				<div className="col-sm-12">
					<div className="element-wrapper">
						<h6 className="element-header">Key Performance Indicator</h6>
						<div className="d-flex justify-content-end w-100">
							<TimeRangeDropdown value={timeRanges.requestMetrics} onChange={handleTimeRangeChange} chartId="requestMetrics" />
						</div>
						<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
							Summary metrics showing overall pharmacy activity and engagement levels.
						</p>
						<div className="element-bo">
							{loadingStates.metrics ? (
								<TableLoading />
							) : (
								<div className="row">
									{pharmacyMetricsConfig.map(({ key, label, color }) => (
										<MetricCard
											key={key}
											label={label}
											// color={color}
											value={requestMetrics?.[key] ?? 0}
											loading={false}
											total={totalValue}
										/>
									))}
								</div>
							)}
						</div>
					</div>
				</div>

				<div className="row">
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Top Requested Medications</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.topRequests} onChange={handleTimeRangeChange} chartId="topRequests" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Displays the top 10 most requested medications, highlighting demand trends to optimize inventory and procurement strategies.
							</p>
							<div className="element-box">
								{loadingStates.topRequests ? (
									<TableLoading />
								) : topRequests.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer width="100%" height="100%">
											<BarChart data={topRequests} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Bar dataKey="requests" fill="#8884d8" name="Medication Requests">
													<LabelList
														dataKey="requests"
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
														formatter={value => `${Number(value)?.toLocaleString()}`}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="row">
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Pharmacy Trends</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.pharmacyTrends} onChange={handleTimeRangeChange} chartId="pharmacyTrends" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart tracks prescription volumes over time to analyze patient demand and operational efficiency.
							</p>
							<div className="element-box">
								{loadingStates.pharmacyTrends ? (
									<TableLoading />
								) : pharmacyTrends.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer>
											<LineChart data={pharmacyTrends} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.pharmacyTrends === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} name="Prescription Count" connectNulls>
													<LabelList
														dataKey="value"
														position="top"
														content={renderCustomLabelAlt}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Line>
											</LineChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="row">
					<div className="col-12">
						<div className="element-wrapper">
							<h6 className="element-header">Slow Moving Drugs Metrics</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.drugWaste} onChange={handleTimeRangeChange} chartId="drugWaste" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart highlights slow moving drugs, showing drugs with low demand trends to optimize inventory and procurement
								strategies.
							</p>
							<div className="element-box">
								{loadingStates.drugWaste ? (
									<TableLoading />
								) : drugWaste.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer width="100%" height="100%">
											<BarChart data={drugWaste} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Bar dataKey="requests" fill="#8884d8" name="Wastage Quantity">
													<LabelList
														dataKey="requests"
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
														formatter={value => `${Number(value)?.toLocaleString()}`}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="row">
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Cancelled Drugs Trends</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.cancelledDrugs} onChange={handleTimeRangeChange} chartId="cancelledDrugs" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Analyzes trends in cancelled drug orders to identify potential issues in prescribing or inventory processes.
							</p>
							<div className="element-box">
								{loadingStates.cancelledDrugs ? (
									<TableLoading />
								) : cancelledDrugs.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer>
											<LineChart data={cancelledDrugs} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.cancelledDrugs === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} name="Cancelled Drugs" connectNulls>
													<LabelList dataKey="value" position="top" style={{ fontSize: '12px', fill: '#333' }} />
												</Line>
											</LineChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="col-sm-12">
					<div className="element-wrapper">
						<h6 className="element-header">Medication Switches/Substitutions</h6>
						<div className="d-flex justify-content-end w-100">
							<TimeRangeDropdown value={timeRanges.substitutions} onChange={handleTimeRangeChange} chartId="substitutions" />
						</div>
						<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
							Monitors the frequency of medication substitutions to assess formulary compliance and patient safety.
						</p>
						<div className="element-box">
							{loadingStates.substitutions ? (
								<TableLoading />
							) : substitutions.length > 0 ? (
								<div style={{ height: '400px' }}>
									<ResponsiveContainer width="100%" height="100%">
										<LineChart data={substitutions} margin={{ top: 0, right: 0, left: 0, bottom: 50 }}>
											<CartesianGrid strokeDasharray="3 3" />
											<XAxis
												dataKey="name"
												angle={-45}
												textAnchor="end"
												fontSize={12}
												interval={timeRanges.substitutions === 'pastQuarter' ? 2 : 0}
												tickFormatter={value => {
													const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
													const capitalized = truncated
														.toLowerCase()
														.split(' ')
														.map(word => word.charAt(0).toUpperCase() + word.slice(1))
														.join(' ');
													return capitalized;
												}}
											/>
											<YAxis domain={[0, 'auto']} />
											<Tooltip
												formatter={value => Number(value).toLocaleString()}
												labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
											/>
											<Legend wrapperStyle={{ marginBottom: '-50px' }} />
											<Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} activeDot={{ r: 8 }} name="Substitutions">
												<LabelList
													dataKey="value"
													position="top"
													offset={10}
													style={{ fontSize: '12px', fill: '#333' }}
													content={renderCustomLabelAlt}
												/>
											</Line>
										</LineChart>
									</ResponsiveContainer>
								</div>
							) : (
								<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
									<p className=" mt-6" style={{ paddingTop: '50px' }}>
										No Record for the selected time
									</p>
								</div>
							)}
						</div>
					</div>
				</div>

				<div className="row">
					<div className="col-12">
						<div className="element-wrapper">
							<h6 className="element-header">Drug Expiry Track</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.drugWaste} onChange={handleTimeRangeChange} chartId="drugWaste" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Identifies medications with the highest wastage rates, enabling better inventory control and strategies to minimize losses.
							</p>
							<div className="element-box">
								{loadingStates.drugWaste ? (
									<TableLoading />
								) : drugWaste.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer width="100%" height="100%">
											<LineChart data={drugWaste} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Line dataKey="requests" fill="#8884d8" name="Drug Expiry">
													<LabelList
														dataKey="requests"
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
														formatter={value => `${Number(value)?.toLocaleString()}`}
													/>
												</Line>
											</LineChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PharmacyStaffAnalytics;
